import { compose, withHandlers, withState } from 'recompose';
import { get } from 'lodash';

import { getMatch, getNewProductMatch } from './actions';
import withShopifySettings from './withShopifySettings';
import { isAssignmentChanged, isSKUChanged } from './components/MaintainSKUChangesModal/checkValuesChanged';
import hasAssignedLocation from './hasAssignmentLocation';
import { PRODUCT_ASSIGNMENT_STEP } from '../../constants';

const withMatch = compose(
  withShopifySettings,
  withState('matchedItems', 'setMatchedItems', []),
  withHandlers({
    fetchMatch: props => async (sku) => {
      const { activeStep, brandId, initialValues, formValues, params, setMatchedItems, shopifySettings } = props;
      const assignmentChanged = isAssignmentChanged(initialValues, formValues);
      const skuChanged = isSKUChanged(initialValues, formValues);
      const prevShopifyLinkedProduct = get(initialValues, 'linkedProducts.shopify');
      const shopifyLinkedProduct = get(formValues, 'linkedProducts.shopify');

      // if sku and assignment not changed and the product already connected we should not show any shopify modal
      if (!assignmentChanged && !skuChanged && shopifyLinkedProduct) {
        return [];
      }

      if (activeStep === PRODUCT_ASSIGNMENT_STEP && shopifySettings.locationId === brandId) {
        return [];
      }

      // if product is not connected and SKU is empty we should not fetch matches
      if (!sku && !shopifyLinkedProduct) {
        return [];
      }

      const matchResponse = params.id ?
        await getMatch(brandId, params.id, sku) :
        await getNewProductMatch(brandId, sku);

      const vendorMatches = get(matchResponse, 'matches', []);
      // remove linkedProducts.shopify for disconnection
      // set a new remote identifier if we need to connect. first one from the array filtered by current identifier.
      // we should also compare current product assignments with location selected in the Shopify settings
      const hadTargetLocationAssigned =
        hasAssignedLocation(initialValues.assignments, shopifySettings.locationId, brandId);
      const hasTargetLocationAssigned =
        hasAssignedLocation(formValues.assignments, shopifySettings.locationId, brandId);

      // for cases when assignment globally not changed.
      if (activeStep === PRODUCT_ASSIGNMENT_STEP && hasTargetLocationAssigned === hadTargetLocationAssigned) {
        return [];
      }

      const shopifyMatches = vendorMatches.filter(match => match.vendor === 'shopify');

      const targetMatches = shopifyMatches
        .map(item => ({
          name: item.data.name,
          remoteIdentifier: item.remoteIdentifier,
          sku: item.data.sku,
          status: item.remoteIdentifier === shopifyLinkedProduct ? 'connected' : 'new',
        }))
        .filter((match) => {
          // can only disconnect
          if (!hasTargetLocationAssigned) {
            return match.status === 'connected';
          }

          // if product newly connected and sku is not empty, filter same sku
          if (!prevShopifyLinkedProduct && shopifyLinkedProduct && formValues.sku) {
            return match.sku !== formValues.sku;
          }

          // if product was disconnected and sku is not empty, filter all except same sku
          if (prevShopifyLinkedProduct && !shopifyLinkedProduct && formValues.sku) {
            return match.sku === formValues.sku;
          }

          return true;
        });

      setMatchedItems(targetMatches);

      return targetMatches;
    },
  })
);

export default withMatch;
